/**
 * OnboardingTooltip Component
 * Highlighted tooltip with spotlight effect for onboarding tours
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { OnboardingStep, OnboardingStepPosition } from '@/types/onboarding';
import { useOnboardingContext } from './OnboardingProvider';

interface OnboardingTooltipProps {
  step: OnboardingStep;
  stepNumber: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious?: () => void;
  onSkip: () => void;
}

interface Position {
  top: number;
  left: number;
  arrowPosition?: string;
}

/**
 * Calculate tooltip position relative to target element
 */
const calculatePosition = (
  targetRect: DOMRect,
  tooltipRect: DOMRect,
  position: OnboardingStepPosition = 'bottom'
): Position => {
  const padding = 16;
  const arrowSize = 8;
  let top = 0;
  let left = 0;
  let arrowPosition = '';

  switch (position) {
    case 'top':
      top = targetRect.top - tooltipRect.height - padding - arrowSize;
      left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
      arrowPosition = 'bottom';
      break;

    case 'bottom':
      top = targetRect.bottom + padding + arrowSize;
      left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
      arrowPosition = 'top';
      break;

    case 'left':
      top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
      left = targetRect.left - tooltipRect.width - padding - arrowSize;
      arrowPosition = 'right';
      break;

    case 'right':
      top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
      left = targetRect.right + padding + arrowSize;
      arrowPosition = 'left';
      break;

    case 'top-start':
      top = targetRect.top - tooltipRect.height - padding - arrowSize;
      left = targetRect.left;
      arrowPosition = 'bottom-start';
      break;

    case 'top-end':
      top = targetRect.top - tooltipRect.height - padding - arrowSize;
      left = targetRect.right - tooltipRect.width;
      arrowPosition = 'bottom-end';
      break;

    case 'bottom-start':
      top = targetRect.bottom + padding + arrowSize;
      left = targetRect.left;
      arrowPosition = 'top-start';
      break;

    case 'bottom-end':
      top = targetRect.bottom + padding + arrowSize;
      left = targetRect.right - tooltipRect.width;
      arrowPosition = 'top-end';
      break;

    case 'left-start':
      top = targetRect.top;
      left = targetRect.left - tooltipRect.width - padding - arrowSize;
      arrowPosition = 'right-start';
      break;

    case 'left-end':
      top = targetRect.bottom - tooltipRect.height;
      left = targetRect.left - tooltipRect.width - padding - arrowSize;
      arrowPosition = 'right-end';
      break;

    case 'right-start':
      top = targetRect.top;
      left = targetRect.right + padding + arrowSize;
      arrowPosition = 'left-start';
      break;

    case 'right-end':
      top = targetRect.bottom - tooltipRect.height;
      left = targetRect.right + padding + arrowSize;
      arrowPosition = 'left-end';
      break;
  }

  // Keep tooltip within viewport
  const viewportPadding = 16;
  if (left < viewportPadding) {
    left = viewportPadding;
  } else if (left + tooltipRect.width > window.innerWidth - viewportPadding) {
    left = window.innerWidth - tooltipRect.width - viewportPadding;
  }

  if (top < viewportPadding) {
    top = viewportPadding;
  } else if (top + tooltipRect.height > window.innerHeight - viewportPadding) {
    top = window.innerHeight - tooltipRect.height - viewportPadding;
  }

  return { top, left, arrowPosition };
};

/**
 * OnboardingTooltip Component
 */
export const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  step,
  stepNumber,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
}) => {
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { setOnboardingComplete } = useOnboardingContext();

  /**
   * Find and track target element
   */
  const updatePosition = useCallback(() => {
    const targetElement = document.querySelector(step.targetSelector);
    if (!targetElement || !tooltipRef.current) {
      setIsVisible(false);
      return;
    }

    const targetBounds = targetElement.getBoundingClientRect();
    const tooltipBounds = tooltipRef.current.getBoundingClientRect();

    setTargetRect(targetBounds);
    const newPosition = calculatePosition(targetBounds, tooltipBounds, step.position);
    setPosition(newPosition);
    setIsVisible(true);

    // Scroll target into view if needed
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }, [step.targetSelector, step.position]);

  /**
   * Update position on mount and when dependencies change
   */
  useEffect(() => {
    // Wait for DOM to settle
    const timer = setTimeout(updatePosition, 100);
    return () => clearTimeout(timer);
  }, [updatePosition]);

  /**
   * Update position on resize and scroll
   */
  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  /**
   * Handle skip with "Don't show again" option
   */
  const handleSkip = useCallback(() => {
    if (dontShowAgain) {
      setOnboardingComplete();
    }
    onSkip();
  }, [dontShowAgain, setOnboardingComplete, onSkip]);

  /**
   * Handle keyboard navigation
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        onNext();
      } else if (e.key === 'ArrowLeft' && onPrevious) {
        onPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, onPrevious, handleSkip]);

  if (!isVisible) {
    return null;
  }

  return createPortal(
    <>
      {/* Spotlight overlay */}
      {step.spotlight && targetRect && (
        <div
          className="fixed inset-0 z-[9998] pointer-events-none"
          style={{
            background: `radial-gradient(
              circle at ${targetRect.left + targetRect.width / 2}px ${
              targetRect.top + targetRect.height / 2
            }px,
              transparent ${Math.max(targetRect.width, targetRect.height) / 2 + 20}px,
              rgba(0, 0, 0, 0.5) ${Math.max(targetRect.width, targetRect.height) / 2 + 120}px
            )`,
          }}
        />
      )}

      {/* Highlighted target border */}
      {step.spotlight && targetRect && (
        <div
          className="fixed z-[9999] pointer-events-none rounded-lg border-2 border-terracotta-500 shadow-lg transition-all duration-300"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[10000] animate-in fade-in slide-in-from-bottom-2 duration-300"
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <div className="relative bg-white dark:bg-espresso-800 rounded-lg shadow-xl border border-neutral-200 dark:border-espresso-700 p-6 max-w-md">
          {/* Arrow */}
          {position.arrowPosition && (
            <div
              className={`absolute w-4 h-4 bg-white dark:bg-espresso-800 border border-neutral-200 dark:border-espresso-700 transform rotate-45 ${
                position.arrowPosition === 'top'
                  ? '-top-2 left-1/2 -translate-x-1/2 border-b-0 border-r-0'
                  : position.arrowPosition === 'bottom'
                  ? '-bottom-2 left-1/2 -translate-x-1/2 border-t-0 border-l-0'
                  : position.arrowPosition === 'left'
                  ? '-left-2 top-1/2 -translate-y-1/2 border-t-0 border-r-0'
                  : position.arrowPosition === 'right'
                  ? '-right-2 top-1/2 -translate-y-1/2 border-b-0 border-l-0'
                  : ''
              }`}
            />
          )}

          {/* Step counter */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-terracotta-600 dark:text-terracotta-400">
              Step {stepNumber} of {totalSteps}
            </span>
            {step.dismissible !== false && (
              <button
                onClick={handleSkip}
                className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                aria-label="Close"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-espresso-900 dark:text-cream-100 mb-2">
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4 leading-relaxed">
            {step.description}
          </p>

          {/* Custom action */}
          {step.action && (
            <button
              onClick={step.action.onClick}
              className="w-full mb-3 px-4 py-2 text-sm font-medium text-terracotta-700 dark:text-terracotta-300 bg-terracotta-50 dark:bg-terracotta-900/20 rounded-md hover:bg-terracotta-100 dark:hover:bg-terracotta-900/30 transition-colors"
            >
              {step.action.label}
            </button>
          )}

          {/* Don't show again checkbox */}
          <label className="flex items-center mb-4 text-xs text-neutral-500 dark:text-neutral-400 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="mr-2 rounded border-neutral-300 dark:border-espresso-600 text-terracotta-600 focus:ring-terracotta-500"
            />
            Don't show onboarding again
          </label>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-2">
              {onPrevious && stepNumber > 1 && (
                <button
                  onClick={onPrevious}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-espresso-700 rounded-md transition-colors"
                >
                  Previous
                </button>
              )}
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
              >
                Skip Tour
              </button>
            </div>
            <button
              onClick={onNext}
              className="px-4 py-2 text-sm font-medium text-white bg-terracotta-600 hover:bg-terracotta-700 rounded-md transition-colors shadow-sm"
            >
              {stepNumber === totalSteps ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};
